import { useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { GridRowModes, GridActionsCellItem, GridToolbarContainer, useGridApiRef } from '@mui/x-data-grid-premium';
import { Field, FieldArray, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import { Box, Typography, Paper } from '@mui/material';
import dayjs from 'dayjs';

import { dataGridPremiumColumns } from '../../utils/helper';
import { DataGridIdentifiers, FormErrorUI, DatagridPremiumUi, InputUI, ButtonUI, AddButtonUi } from '../Interface';

import documentService from '../../service/newDoc.service';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import { InvManagementMethod } from '../../config/itemGroup';

const BatchOut = ({ disabled, selectedLine, defaultBatchLine, onClose, autocompleteData, open }) => {
  const apiRef = useGridApiRef();
  const { values, validateField, setFieldValue, errors } = useFormikContext();
  const [batches, setBatches] = useState([]);

  const selectedLineIndex = selectedLine?.selectedLineIndex;
  const item = autocompleteData?.item?.find((i) => i.itemCode === values.docLine[selectedLineIndex]?.itemCode);
  const isBatchManaged = item?.invManagementMethod === InvManagementMethod.BATCH;
  const whsCode = values.docLine[selectedLineIndex]?.whsCode ?? values.whsCode;
  const whs = autocompleteData?.warehouse?.find((w) => w.code === whsCode);
  const isBinManaged = Boolean(whs?.binManaged);

  const getItemStock = () => {
    documentService
      .getItemStock({
        whsCode,
        itemCode: values.docLine[selectedLineIndex]?.itemCode,
      })
      .then((data) => {
        setBatches(data);
      });
  };

  useEffect(() => {
    if (typeof selectedLineIndex !== 'number') return;
    if (!isBatchManaged && !isBinManaged) return;
    /*if (!values.whsCode) {
      return;
    }*/
    if (!values.docLine?.[selectedLineIndex]?.itemCode) return;

    getItemStock();
  }, [isBatchManaged, isBinManaged, selectedLineIndex, values.whsCode, values.docLine?.[selectedLineIndex]?.itemCode]);

  useEffect(() => {
    if (typeof selectedLineIndex !== 'number') return;
    if (!open || !whsCode) return;
  }, [open, selectedLineIndex, whsCode]);

  if (!selectedLine || typeof selectedLineIndex !== 'number') return null;

  const defaultBinLine = {
    id: null,
    binCode: '',
    binId: null,
    snBinId: null,
    quantity: '',
  };

  const getLineIndex = (lines, id) => {
    return lines.findIndex((rc) => rc.id === id);
  };

  const isNegativeNumberInput = (value) => {
    if (value === '' || value === null || value === undefined) return false;

    const parsed = Number(value);
    return !Number.isNaN(parsed) && parsed < 0;
  };

  const isNonPositiveNumber = (value) => {
    if (value === '' || value === null || value === undefined) return false;

    const parsed = Number(value);
    return !Number.isNaN(parsed) && parsed <= 0;
  };

  const upsertBinForBatch = (batchId, binCode, binId, quantity) => {
    const bins = [...(values.docLine[selectedLineIndex]?.docLineBin ?? [])];
    const idx = bins.findIndex((b) => b.snBinId === batchId);
    const updated = {
      ...(idx >= 0 ? bins[idx] : { id: uuidv4() }),
      binCode,
      binId,
      snBinId: batchId,
      quantity,
    };

    if (idx >= 0) {
      bins[idx] = updated;
    } else {
      bins.push(updated);
    }

    setFieldValue(`docLine.${selectedLineIndex}.docLineBin`, bins);
  };

  const handleAddClick = async (row, push) => {
    const newLine = {
      ...defaultBatchLine,
      id: uuidv4(),
      batchSn: row.batchSn,
    };
    await push(newLine);
    apiRef.current.startRowEditMode({ id: newLine.id });

    if (isBinManaged) {
      const rowBinCode = row.binCode ?? '';
      const rowBinId = row.binId ?? null;
      if (rowBinCode || rowBinId) {
        upsertBinForBatch(newLine.id, rowBinCode, rowBinId, newLine.quantity ?? '');
      }
    }

    const fieldsToValidate = [
      `docLine.${selectedLineIndex}.docLineBatchSn.${values.docLine[selectedLineIndex]?.docLineBatchSn?.length ?? 0}.batchSn`,
      `docLine.${selectedLineIndex}.docLineBatchSn.${values.docLine[selectedLineIndex]?.docLineBatchSn?.length ?? 0}.quantity`,
    ];

    for (const field of fieldsToValidate) {
      await validateField(field);
    }
  };

  const handleAddBin = async (push) => {
    const newLine = { ...defaultBinLine, id: uuidv4() };
    await push(newLine);
    apiRef.current.startRowEditMode({ id: newLine.id });
  };

  const handleAddBinFromStock = async (row, push) => {
    const newLine = {
      ...defaultBinLine,
      id: uuidv4(),
      binCode: row.binCode ?? '',
      binId: row.binId ?? null,
    };
    await push(newLine);
    apiRef.current.startRowEditMode({ id: newLine.id });
  };

  const stockColumns = (push) => {
    return dataGridPremiumColumns([
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Szerk.',
        cellClassName: 'actions',
        sortable: false,
        getActions: ({ id, row }) => {
          return [
            <GridActionsCellItem
              icon={<AddIcon />}
              label="Delete"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleAddClick(row, push);
              }}
              color="inherit"
              disabled={disabled}
            />,
          ];
        },
      },
      {
        field: 'batchSn',
        headerName: 'Sarzs',
        type: 'string',
        sortable: false,
      },
      ...(isBinManaged
        ? [
            {
              field: 'binCode',
              headerName: 'Rakhely',
              type: 'string',
              sortable: false,
            },
          ]
        : []),
      {
        field: 'effectiveQty',
        headerName: 'Mennyiség',
        type: 'number',
        sortable: false,
      },
      {
        field: 'lot',
        headerName: 'LOT',
        type: 'string',
        sortable: false,
      },
      {
        field: 'supplierId',
        headerName: 'Beszállító azon.',
        type: 'string',
        sortable: false,
      },
      {
        field: 'expDate',
        headerName: 'Lejárat',
        type: 'date',

        sortable: false,
        valueGetter: (_data, row) => {
          return row.expDate ? new Date(row.expDate) : null;
        },
        renderCell: (data) => {
          return data.row.expDate ? dayjs(data.row.expDate).format('YYYY.MM.DD') : '';
        },
      },
      {
        field: 'mnfDate',
        headerName: 'Gyártás',
        type: 'date',
        sortable: false,
        valueGetter: (_data, row) => {
          return row.mnfDate ? new Date(row.mnfDate) : null;
        },
        renderCell: (data) => {
          return data.row.mnfDate ? dayjs(data.row.mnfDate).format('YYYY.MM.DD') : '';
        },
      },
      {
        field: 'batchAttribute1',
        headerName: 'Attribútum 1',
        type: 'string',
        sortable: false,
      },
      {
        field: 'batchAttribute2',
        headerName: 'Attribútum 2',
        type: 'string',
        sortable: false,
      },
      {
        field: 'comment',
        headerName: 'Megjegyzés',
        type: 'string',
        sortable: false,
      },
      {
        field: 'price',
        headerName: 'Ár',
        type: 'string',
        sortable: false,
        valueGetter: (data, row) => {
          return row.price ?? '';
        },
      },
      {
        field: 'other',
        headerName: 'Egyéb',
        type: 'string',
        sortable: false,
      },
    ]);
  };

  const binStockColumns = (push) =>
    dataGridPremiumColumns([
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Szerk.',
        cellClassName: 'actions',
        sortable: false,
        getActions: ({ row }) => {
          return [
            <GridActionsCellItem
              icon={<AddIcon />}
              label="Delete"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleAddBinFromStock(row, push);
              }}
              color="inherit"
              disabled={disabled}
            />,
          ];
        },
      },
      {
        field: 'binCode',
        headerName: 'Rakhely',
        type: 'string',
        sortable: false,
      },
      {
        field: 'effectiveQty',
        headerName: 'Mennyiség',
        type: 'number',
        sortable: false,
      },
    ]);

  const columns = (setFieldValue, validateField, errors, remove) =>
    dataGridPremiumColumns([
      ...(isBinManaged
        ? [
            {
              field: 'binCode',
              headerName: 'Rakhely',
              type: 'string',
              valueGetter: (_data, row) => {
                if (!isBatchManaged) return row.binCode ?? '';
                // Új sor esetén nincs batchId, de lehet binCode
                const batchIndex = getLineIndex(values.docLine[selectedLineIndex].docLineBatchSn, row.id);
                let batchId = null;
                if (batchIndex >= 0) {
                  batchId = values.docLine[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.id ?? null;
                }
                let bin = null;
                if (batchId) {
                  bin = (values.docLine[selectedLineIndex]?.docLineBin ?? []).find((b) => b.snBinId === batchId);
                } else {
                  // Ha új sor, próbáljuk a row.id alapján
                  bin = (values.docLine[selectedLineIndex]?.docLineBin ?? []).find((b) => b.snBinId === row.id);
                }
                return bin?.binCode ?? row.binCode ?? '';
              },
            },
          ]
        : []),
      ...(isBatchManaged
        ? [
            {
              field: 'batchSn',
              headerName: 'Sarzs',
              type: 'string',
            },
            {
              field: 'quantity',
              headerName: 'Mennyiség',
              type: 'number',
              editable: true,
              renderEditCell: (data) => {
                const batchIndex = getLineIndex(values.docLine[selectedLineIndex].docLineBatchSn, data.id);
                return (
                  <Field
                    name={`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.quantity`}
                    type="number"
                    variant="standard"
                    as={InputUI}
                    fullWidth
                    inputProps={{ min: 0.0000001, step: 'any' }}
                    helperText={
                      <FormErrorUI
                        message={errors?.docLine?.[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.quantity}
                      />
                    }
                    onKeyDown={(e) => {
                      if (e.key === '-') {
                        e.preventDefault();
                      }
                    }}
                    onChange={(e) => {
                      if (isNegativeNumberInput(e.target.value)) return;

                      setFieldValue(
                        `docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.quantity`,
                        e.target.value ?? ''
                      );
                      if (isBinManaged) {
                        const batchId = values.docLine[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.id ?? null;
                        const currentBin = (values.docLine[selectedLineIndex]?.docLineBin ?? []).find(
                          (b) => b.snBinId === batchId
                        );
                        if (currentBin) {
                          upsertBinForBatch(
                            batchId,
                            currentBin.binCode ?? '',
                            currentBin.binId ?? null,
                            e.target.value ?? ''
                          );
                        }
                      }
                      data.api.setEditCellValue({
                        id: data.id,
                        field: data.field,
                        value: e.target.value,
                      });
                    }}
                    onBlur={async () => {
                      const path = `docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.quantity`;
                      if (
                        isNonPositiveNumber(values.docLine[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.quantity)
                      ) {
                        setFieldValue(path, '');
                      }
                      await validateField(path);
                    }}
                  />
                );
              },
            },
          ]
        : [
            {
              field: 'quantity',
              headerName: 'Mennyiség',
              type: 'number',
              editable: true,
              renderEditCell: (data) => {
                const lineIndex = getLineIndex(values.docLine[selectedLineIndex].docLineBin, data.id);
                return (
                  <Field
                    name={`docLine.${selectedLineIndex}.docLineBin.${lineIndex}.quantity`}
                    type="number"
                    variant="standard"
                    as={InputUI}
                    fullWidth
                    inputProps={{ min: 0.0000001, step: 'any' }}
                    helperText={
                      <FormErrorUI message={errors?.docLine?.[selectedLineIndex]?.docLineBin?.[lineIndex]?.quantity} />
                    }
                    onKeyDown={(e) => {
                      if (e.key === '-') {
                        e.preventDefault();
                      }
                    }}
                    onChange={(e) => {
                      if (isNegativeNumberInput(e.target.value)) return;

                      setFieldValue(
                        `docLine.${selectedLineIndex}.docLineBin.${lineIndex}.quantity`,
                        e.target.value ?? ''
                      );
                      data.api.setEditCellValue({
                        id: data.id,
                        field: data.field,
                        value: e.target.value,
                      });
                    }}
                    onBlur={async () => {
                      const path = `docLine.${selectedLineIndex}.docLineBin.${lineIndex}.quantity`;
                      if (isNonPositiveNumber(values.docLine[selectedLineIndex]?.docLineBin?.[lineIndex]?.quantity)) {
                        setFieldValue(path, '');
                      }
                      await validateField(path);
                    }}
                  />
                );
              },
            },
          ]),
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Szerk.',
        cellClassName: 'actions',
        sortable: false,
        getActions: ({ id }) => [
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              if (isBatchManaged) {
                // Sarzs törlés
                const batchSnLines = values.docLine[selectedLineIndex].docLineBatchSn || [];
                const binLines = values.docLine[selectedLineIndex].docLineBin || [];
                const lineIndex = getLineIndex(batchSnLines, id);
                const batch = batchSnLines[lineIndex];
                // Töröljük a kapcsolódó bin hozzárendelést is
                if (batch && batch.id && isBinManaged) {
                  const newBinLines = binLines.filter((b) => b.snBinId !== batch.id);
                  setFieldValue(`docLine.${selectedLineIndex}.docLineBin`, newBinLines);
                }
                remove(lineIndex);
              } else {
                // Bin törlés
                const binLines = values.docLine[selectedLineIndex].docLineBin || [];
                const batchSnLines = values.docLine[selectedLineIndex].docLineBatchSn || [];
                const lineIndex = getLineIndex(binLines, id);
                const bin = binLines[lineIndex];
                // Ha van kapcsolódó sarzs, azt is töröljük
                if (bin && bin.snBinId && isBatchManaged) {
                  const newBatchSnLines = batchSnLines.filter((b) => b.id !== bin.snBinId);
                  setFieldValue(`docLine.${selectedLineIndex}.docLineBatchSn`, newBatchSnLines);
                }
                remove(lineIndex);
              }
            }}
            color="inherit"
            disabled={disabled}
          />,
        ],
      },
    ]);

  const handleSave = () => {
    onClose();
  };

  const handleCancel = () => {
    setFieldValue(`docLine.${selectedLine.selectedLineIndex}.docLineBatchSn`, selectedLine.docLineBatchSn || []);
    setFieldValue(`docLine.${selectedLine.selectedLineIndex}.docLineBin`, selectedLine.docLineBin || []);
    onClose();
  };

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box className="align-middle">
        <Paper elevation={1} sx={{ overflow: 'hidden', borderRadius: 2 }}>
          <Box display="flex" gap={2} p={2} alignItems="center" bgcolor="#f5f5f5" mb={2}>
            <Box flex={0.35} minWidth={0} display="flex" alignItems="center">
              <Typography fontWeight="bold">Sor:&nbsp;</Typography>
              <Typography>{selectedLineIndex + 1}</Typography>
            </Box>
            <Box flex={1} minWidth={0} display="flex" alignItems="center">
              <Typography fontWeight="bold">Cikkszám:&nbsp; </Typography>
              <Typography>{values.docLine[selectedLineIndex]?.itemCode ?? ''}</Typography>
            </Box>
            <Box flex={1} minWidth={0} display="flex" alignItems="center">
              <Typography fontWeight="bold">Raktár:&nbsp; </Typography>
              <Typography>{values.docLine[selectedLineIndex]?.whsCode ?? ''}</Typography>
            </Box>
            <Box flex={1} minWidth={0} display="flex" alignItems="center">
              <Typography fontWeight="bold">Nyitott mennyiség:&nbsp; </Typography>
              <Typography>{values.docLine[selectedLineIndex]?.qty ?? ''}</Typography>
            </Box>
            <Box flex={1} minWidth={0} display="flex" alignItems="center">
              <Typography fontWeight="bold">
                {isBatchManaged ? 'Összesített sarzs: ' : 'Összesített rakhely: '}&nbsp;
              </Typography>
              <Typography>
                {(isBatchManaged
                  ? values.docLine[selectedLineIndex]?.docLineBatchSn ?? []
                  : values.docLine[selectedLineIndex]?.docLineBin ?? []
                ).reduce((sum, line) => sum + (Number(line.quantity) || 0), 0)}
              </Typography>
            </Box>
          </Box>

          <FieldArray
            name={
              isBatchManaged ? `docLine.${selectedLineIndex}.docLineBatchSn` : `docLine.${selectedLineIndex}.docLineBin`
            }
          >
            {({ remove, push }) => (
              <Box display="flex" gap={2}>
                <Box flex={1} minWidth={0}>
                  <DatagridPremiumUi
                    datagridIdentifier={
                      isBinManaged && !isBatchManaged
                        ? DataGridIdentifiers.BinInStockTable
                        : isBatchManaged && !isBinManaged
                          ? DataGridIdentifiers.BatchInStockTable
                          : DataGridIdentifiers.BinBatchInStockTable
                    }
                    height={360}
                    columns={isBatchManaged ? stockColumns(push) : binStockColumns(push)}
                    rows={batches ?? []}
                    slots={{
                      toolbar: null,
                    }}
                    onCellDoubleClick={(_params, event) => {
                      event.defaultMuiPrevented = true;
                    }}
                    headerFilters={false}
                    getRowHeight={() => 'auto'}
                  />
                </Box>
                <Box flex={1} minWidth={0}>
                  <DatagridPremiumUi
                    apiRef={apiRef}
                    datagridIdentifier={
                      isBinManaged && !isBatchManaged
                        ? DataGridIdentifiers.BinOutTable
                        : isBatchManaged && !isBinManaged
                          ? DataGridIdentifiers.BatchOutTable
                          : DataGridIdentifiers.BinBatchOutTable
                    }
                    height={360}
                    columns={columns(setFieldValue, validateField, errors, remove)}
                    rows={
                      isBatchManaged
                        ? values.docLine[selectedLineIndex]?.docLineBatchSn ?? []
                        : values.docLine[selectedLineIndex]?.docLineBin ?? []
                    }
                    editMode="row"
                    onCellDoubleClick={(_params, event) => {
                      event.defaultMuiPrevented = true;
                    }}
                    headerFilters={false}
                    getRowHeight={() => 'auto'}
                    onCellClick={(params, event) => {
                      const rowsInEditMode = Object.keys(apiRef.current.state.editRows);

                      for (const rowId of rowsInEditMode) {
                        if (params.row.id.toString() !== rowId) {
                          apiRef.current.stopRowEditMode({ id: rowId });
                        }
                      }

                      const rowMode = apiRef.current.getRowMode(params.row.id);
                      if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                    }}
                  />
                </Box>
              </Box>
            )}
          </FieldArray>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
            <ButtonUI text="Mentés" className="bg-success" onClick={handleSave} />
            <ButtonUI text="Mégse" color="blue-gray" variant="gradient" onClick={handleCancel} />
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default BatchOut;
